import { useEffect, useState } from 'react';

/**
 * The green-room preview transport, shared between the player that owns
 * the toggle ([`PreviewPlayer`](../components/PreviewPlayer.tsx)) and
 * anything else whose behaviour depends on how far behind the operator's
 * picture is — today that's the talkback control, which warns when the
 * operator is about to hold a conversation against a 6-8 s HLS delay.
 *
 * Persisted per-operator in localStorage. Defaults to HLS so operators
 * behind UDP-restricted networks get a working preview on first paint;
 * WebRTC is the opt-in once they've confirmed the network supports it.
 */
export type PreviewMode = 'webrtc' | 'hls';

const MODE_PREF_KEY = 'live-preview:mode';

/**
 * Same-tab change notification. The `storage` event only fires in OTHER
 * tabs, so without this a talkback control sitting next to the player
 * whose toggle was just clicked would keep showing the stale transport
 * until something else re-rendered it.
 */
const MODE_EVENT = 'live-preview:mode-change';

export function readModePref(): PreviewMode {
  try {
    const raw = localStorage.getItem(MODE_PREF_KEY);
    if (raw === 'webrtc' || raw === 'hls') return raw;
  } catch {
    /* private mode etc. — fall through to the default */
  }
  return 'hls';
}

export function writeModePref(mode: PreviewMode): void {
  try {
    localStorage.setItem(MODE_PREF_KEY, mode);
  } catch {
    /* private mode etc. — the in-memory state still works this session */
  }
  // Notify same-tab listeners; cross-tab ones get the `storage` event.
  window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: mode }));
}

/** Reactive read of the persisted transport preference. */
export function usePreviewMode(): PreviewMode {
  const [mode, setMode] = useState<PreviewMode>(readModePref);
  useEffect(() => {
    const sync = () => setMode(readModePref());
    window.addEventListener(MODE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(MODE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return mode;
}

/**
 * Whether the preview plays the correspondent's audio. Shared for the
 * same reason as the transport: the talkback control needs to know
 * whether the operator can actually hear the replies to what they say.
 *
 * Both transports carry audio — HLS natively, WebRTC via SRS's own
 * AAC→Opus transcode in its `rtmp_to_rtc` bridge. Defaults to off so a
 * green room isn't suddenly loud on first load; the click that turns it
 * on is also the user gesture browsers require for unmuted playback.
 */
const AUDIO_PREF_KEY = 'live-preview:audio';
const AUDIO_EVENT = 'live-preview:audio-change';

export function readAudioPref(): boolean {
  try {
    return localStorage.getItem(AUDIO_PREF_KEY) === 'on';
  } catch {
    return false;
  }
}

export function writeAudioPref(on: boolean): void {
  try {
    localStorage.setItem(AUDIO_PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode etc. — the in-memory state still works this session */
  }
  window.dispatchEvent(new CustomEvent(AUDIO_EVENT, { detail: on }));
}

/** Reactive read of the persisted preview-audio preference. */
export function usePreviewAudio(): boolean {
  const [on, setOn] = useState<boolean>(readAudioPref);
  useEffect(() => {
    const sync = () => setOn(readAudioPref());
    window.addEventListener(AUDIO_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(AUDIO_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return on;
}
